import React, { useState } from 'react';
import { SegmentedControl } from '@mantine/core';
import Button from '@srm/ui/button';
import Hint from '@srm/ui/hint';
import Modal from '@srm/ui/modal';
import FormGroup from '@srm/ui/form-group';
import {
    AssignedResult,
    IMatrixCell,
    IMatrixParticipant,
    IMatrixPosition,
    IWinnerMatrix,
} from '../WinnerSelection2.types';
import { parseQuantityInput, quantityErrorOf } from '../utils/dialogForm';
import { exceedsRequired, formatQuantity } from '../utils/matrixView';
import { DialogContext } from './DialogContext';
import { QuantityField } from './DialogFields';
import MatrixWarning from './MatrixWarning';

interface IProps {
    matrix: IWinnerMatrix;
    cell: IMatrixCell;
    position: IMatrixPosition;
    participant: IMatrixParticipant;
    busy: boolean;
    blocked?: boolean;
    error: string | undefined;
    onChoose: (result: AssignedResult, quantity: number) => void;
    onClose: () => void;
}

/** Выбор основного или резервного предложения; ранг на доступность не влияет. */
const ChooseOfferDialog: React.FC<IProps> = ({
    matrix,
    cell,
    position,
    participant,
    busy,
    blocked,
    error,
    onChoose,
    onClose,
}) => {
    const [result, setResult] = useState<AssignedResult | null>(null);
    const initial = cell.offeredQuantity ?? cell.selectedQuantity;
    const [quantity, setQuantity] = useState(initial == null ? '' : String(initial));
    const [submitted, setSubmitted] = useState(false);
    const fixed = cell.quantityFixed;
    const parsed = fixed ? cell.offeredQuantity : parseQuantityInput(quantity);
    const quantityError = quantityErrorOf(parsed, cell.offeredQuantity, submitted);
    const unit = cell.unit ?? position.unit;
    const overRequired = exceedsRequired(matrix, cell, position, result ?? 'main', parsed);

    const choose = () => {
        setSubmitted(true);
        if (parsed == null || quantityErrorOf(parsed, cell.offeredQuantity, true) || result == null) return;
        onChoose(result, parsed);
    };

    const footer = (
        <div className="ws2-dialog__footer">
            <Button variant="outline-neutral" disabled={busy} onClick={onClose}>
                Отмена
            </Button>
            <Button variant="primary" disabled={busy || blocked || result == null} loading={busy} onClick={choose}>
                Применить
            </Button>
        </div>
    );

    return (
        <Modal
            className="ws2-modal"
            width={640}
            size="m"
            header="Оценить предложение"
            onClose={onClose}
            hasUnsavedInput={() => result !== null || quantity !== (initial == null ? '' : String(initial))}
            closeOnEscape={!busy}
            closeDisabled={busy}
            footer={footer}
        >
            <div className="ws2-dialog">
                <DialogContext supplier={participant.name} subject={position.name} />
                {error && <Hint role="alert" title={error} tone="danger" maxWidth="100%" />}
                <div className="ws2-dialog__numbers">
                    <div>
                        <span>Предложено участником</span>
                        <strong>
                            {formatQuantity(cell.offeredQuantity)} {unit}
                        </strong>
                    </div>
                    <div>
                        <span>Потребность позиции</span>
                        <strong>
                            {formatQuantity(position.requiredQuantity)} {position.unit}
                        </strong>
                    </div>
                </div>
                <QuantityField
                    value={fixed ? String(cell.offeredQuantity ?? '') : quantity}
                    unit={unit}
                    disabled={fixed || busy || blocked}
                    invalid={!!quantityError}
                    errorMessage={quantityError}
                    onChange={setQuantity}
                />
                {fixed && (
                    <p className="ws2-dialog__note">В этом лоте выбирается количество, предложенное участником</p>
                )}
                <FormGroup label="Результат выбора">
                    <SegmentedControl
                        className="ws2-dialog__result-segments"
                        aria-label="Результат выбора предложения"
                        fullWidth
                        radius="sm"
                        data={[
                            { value: 'main', label: 'Основной' },
                            { value: 'reserve', label: 'Резервный' },
                        ]}
                        value={result ?? ''}
                        disabled={busy || blocked}
                        onChange={(value) => setResult(value as AssignedResult)}
                    />
                </FormGroup>
                <p className="ws2-dialog__note">Основной и резервный объём учитываются отдельно</p>
                {result && overRequired && (
                    <MatrixWarning>
                        {result === 'main'
                            ? 'Основной объём с учётом других назначений превысит потребность'
                            : 'Резервный объём с учётом других назначений превысит потребность'}
                    </MatrixWarning>
                )}
            </div>
        </Modal>
    );
};

export default ChooseOfferDialog;
